/**
 * Brand configuration.
 *
 * A deployment is branded along two axes, and they are deliberately separate:
 *
 *   the PROFILE   which customer this deployment is for — the name in the tab, the mark in
 *                 the rail, what the assistant is called, and a set of default seed colours.
 *                 Named, compiled in, chosen by id (`ralph`, `evt`, `neoenergia`).
 *   the SEEDS     four colours the whole palette derives from. Free-form hex, overridable
 *                 per deployment on top of whatever the profile brought.
 *
 * The split is what keeps one image serving every customer. A profile is artwork and copy,
 * which cannot come from an environment variable; the seeds are the part an operator
 * genuinely wants to nudge on site, and they stay free-form so nudging one needs no release.
 *
 * This module is the only thing that turns *configuration* into either, and it is
 * deliberately boring: read some strings, refuse anything that is not a colour or a known
 * brand id, emit CSS.
 *
 * ── Where configuration arrives from, and what beats what ──
 *
 *   1. the shipped defaults        `@layer theme` in `styles/tokens.css`
 *   2. the profile's seeds         `:root[data-brand='…']`, unlayered, emitted into the head
 *                                  by the Vite plugin — beats (1) because an unlayered rule
 *                                  outranks every layered one
 *   3. build-time `VITE_BRAND_*`   a `style` attribute on `<html>`, written by the same
 *                                  plugin — inline, so it beats (2)
 *   4. run-time `RALPH_BRAND_*`    `boot.js` sets the same properties on the element — also
 *                                  inline but later, so it beats (3)
 *
 * Every step is strictly stronger than the one above it, and none of them relies on which
 * stylesheet the bundler happened to emit first. Run time winning is the point: re-colouring
 * a customer's install must not require rebuilding an image.
 *
 * ── On validation ──
 *
 * The seed lands inside a `<style>` block that we generate, so a value containing `}` or
 * `<` would escape the declaration and inject arbitrary CSS — or close the style element and
 * open a script one. The regex below is the boundary: six or three hex digits and nothing
 * else. It is not a nicety, and it is why this returns `null` for a malformed value rather
 * than trying to repair it — a deployment with a typo in its brand colour should look like
 * the default product, not like a broken one.
 *
 * The brand id is checked the same way and for the same reason: it is written into a
 * selector and onto an attribute, so it is matched against the known ids rather than
 * sanitised. An unknown id is not a half-brand — it is the stock product.
 */

export const BRAND_SEED_KEYS = ['primary', 'grounded', 'alert', 'surface'] as const

export type BrandSeedKey = (typeof BRAND_SEED_KEYS)[number]

export type BrandSeeds = Partial<Record<BrandSeedKey, string>>

/** Every seed, as a profile must state them — no channel may be left to chance. */
export type BrandPalette = Record<BrandSeedKey, string>

/** The reference palette. Kept here as documentation and as the fallback the tests assert. */
export const BRAND_DEFAULTS: BrandPalette = {
  primary: '#6f4fd8',
  grounded: '#0d8b7b',
  alert: '#c4552f',
  surface: '#eae5df'
}

/**
 * The brand profiles this image was built with.
 *
 * `ralph` first, and it is the fallback for anything unrecognised: an id nobody knows about
 * must render the stock product rather than a half-branded one.
 */
export const BRAND_IDS = ['ralph', 'evt', 'neoenergia'] as const

export type BrandId = (typeof BRAND_IDS)[number]

export const DEFAULT_BRAND_ID: BrandId = 'ralph'

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

/**
 * `#ABC`, `abc123`, `#AbC123` → `#aabbcc` / `#abc123`. Anything else → `null`.
 *
 * Shorthand is expanded rather than passed through, so the value written into the document
 * is always in one form and a snapshot of it cannot depend on which notation an operator
 * happened to type.
 */
export const normaliseHex = (raw: string | null | undefined): string | null => {
  if (typeof raw !== 'string') return null
  const digits = HEX.exec(raw.trim())?.[1]?.toLowerCase()
  if (!digits) return null
  if (digits.length === 3) {
    return `#${digits.replace(/./g, (d) => d + d)}`
  }
  return `#${digits}`
}

/**
 * `evt`, `  EVT  `, `Neoenergia` → the id. Anything unknown → `null`.
 *
 * Matched against the known ids rather than sanitised, because this value is written into a
 * CSS selector and onto a DOM attribute. A typo'd id therefore falls back to the stock
 * product, which is a state somebody notices, instead of to a selector that matches nothing,
 * which is a state that looks like the colours simply failed to load.
 */
export const normaliseBrandId = (raw: string | null | undefined): BrandId | null => {
  if (typeof raw !== 'string') return null
  const value = raw.trim().toLowerCase()
  return (BRAND_IDS as readonly string[]).includes(value) ? (value as BrandId) : null
}

/** The id configured under `${prefix}ID`, or `null` when this deployment names no brand. */
export const readBrandId = (env: Record<string, string | undefined>, prefix: string): BrandId | null =>
  normaliseBrandId(env[`${prefix}ID`])

/**
 * Pull the seeds out of an environment map.
 *
 * `prefix` is `VITE_BRAND_` at build time and `RALPH_BRAND_` at run time; the suffixes are
 * the same four words in both, so an operator who has read one has read both.
 */
export const readBrandSeeds = (env: Record<string, string | undefined>, prefix: string): BrandSeeds => {
  const seeds: BrandSeeds = {}
  for (const key of BRAND_SEED_KEYS) {
    const value = normaliseHex(env[`${prefix}${key.toUpperCase()}`])
    if (value) seeds[key] = value
  }
  return seeds
}

/** The four declarations, in a stable key order, without the surrounding block. */
const declarations = (seeds: BrandSeeds): string =>
  BRAND_SEED_KEYS.filter((key) => seeds[key])
    .map((key) => `--brand-${key}:${seeds[key]}`)
    .join(';')

/**
 * The `:root` block for a set of seeds, or `''` when none were configured.
 *
 * Empty rather than a block of defaults on purpose: the defaults already live in
 * `tokens.css`, and emitting them a second time would mean two files to keep in step.
 */
export const brandSeedCss = (seeds: BrandSeeds): string => {
  const body = declarations(seeds)
  return body === '' ? '' : `:root{${body}}`
}

/**
 * The same four properties as a `style` attribute value, for the `<html>` element.
 *
 * Build-time seeds go on the element rather than into a stylesheet so they outrank the
 * profile blocks below without needing a specificity trick — and so run-time seeds, which
 * `boot.js` writes to the same element afterwards, outrank them in turn by being later.
 */
export const brandSeedStyleAttribute = (seeds: BrandSeeds): string => declarations(seeds)

/**
 * One unlayered `:root[data-brand='…']` block per profile, for the document head.
 *
 * Every profile is emitted, not just the one this build names, because the brand is chosen
 * at *run* time: `boot.js` stamps `data-brand` from the container's environment before the
 * first paint, and whichever id it stamps has to find its colours already parsed. That is
 * the whole reason this is a static table in the page rather than a lookup somewhere.
 *
 * Unlayered on purpose. `tokens.css` keeps the shipped seeds in `@layer theme`, and an
 * unlayered rule beats every layered one regardless of source order — so this wins over the
 * defaults without depending on which stylesheet the bundler emitted first.
 */
export const brandProfileCss = (palettes: Record<string, BrandPalette>): string =>
  Object.entries(palettes)
    .filter(([id]) => normaliseBrandId(id) !== null && id !== DEFAULT_BRAND_ID)
    .map(([id, palette]) => `:root[data-brand='${id}']{${declarations(palette)}}`)
    .join('')
